define((require) => {
    "use strict";

    const $             = require("jquery");
    const Class         = require("shared/util/Class");
    const Marionette    = require("marionette");
    const Size          = require("shared/util/Size");
    const Rectangle     = require("shared/util/Rectangle");
    const Scale         = require("shared/util/Scale");
    const Browser       = require("shared/services/Browser");
    const Window        = require("shared/services/Window");
    const {Logger}      = require("shared/util/Logger");
    const Promise       = require("shared/util/Promise");
    const Preconditions = require("shared/util/Preconditions");
    const MathUtil      = require("shared/util/MathUtil").default;
    const channelBridge = require("shared/ui/viewer/ChannelBridge");

    /**
     * The maximum period of time that the timer will run before timeout.
     *
     * @type {Number}
     */
    const INVALIDATE_VIEWPORT_SIZE_TIMEOUT = 5000;

    /**
     * The polling interval for checking the viewport size.
     *
     * @type {Number}
     */
    const INVALIDATE_VIEWPORT_SIZE_INTERVAL = 500;

    const log = new Logger("ResponsiveViewerController");

    /**
     * Internal convenience class used for checking the parent viewport size. <p>
     *
     * This mostly makes sense for embedded experiences, where the viewport size may
     * not be ready at initialization time.
     *
     * The timer will timeout after <tt>INVALIDATE_VIEWPORT_SIZE_TIMEOUT</tt>
     * milliseconds, *resolving* the internal deferred instance with a value of "timeout".
     * On success, the resolved value will be "valid" and if forcefully stopped the
     * resolved value is "stopped".
     *
     * @class ValidSizeTimer
     */
    const ValidSizeTimer = Class.extend({

        /**
         * @type {Function}
         */
        _predicate: null,

        /**
         * @type {Deferred}
         */
        _deferred: null,

        /**
         * @type {Number}
         */
        _interval: null,

        /**
         * @type {Number}
         */
        _timeout: null,

        /**
         * Constructor.
         *
         * @param  {Function} predicate A predicate function to check for every
         *                              interval. If the function returns <tt>true</tt>,
         *                              the internal deferred object will be resolved.
         *
         * @see #promise
         */
        constructor(predicate) {
            this._predicate = predicate;
            this._deferred  = Promise.deferred();
        },

        /**
         * Starts the timer if not already started, returning a promise that will resolve on a valid size, but
         * rejected after the timeout in case no valid size could be detected. <p>
         *
         * This timer can be restarted if not currently running, e.g. after it has been stopped. If so, a new deferred
         * will be used internally, returning fresh promises.
         *
         * @return {Promise} A promise to be resolved (now) when a valid size is obtained, or rejected after the
         *                   configured timeout otherwise. The promise may already have been resolved, i.e. if the
         *                   size is already valid at the point in time this is called (no need to start the timer).
         * @see    #running
         * @see    #stop
         */
        start() {
            this._timeout  = setTimeout(this._end.bind(this, true), INVALIDATE_VIEWPORT_SIZE_TIMEOUT);
            this._interval = setInterval(this._tick.bind(this), INVALIDATE_VIEWPORT_SIZE_INTERVAL);

            log.warn("Invalid viewport size at start time - starting size invalidation timer");
            return this._deferred.promise();
        },

        /**
         *
         */
        stop(cause) {
            if (this._interval) {
                clearInterval(this._interval);
                clearTimeout(this._timeout);
                this._interval = null;
                this._timeout  = null;

                this._deferred.resolve(cause || "stopped"); // not necessarily an error, so notify listeners about this!
            }
        },

        _tick() {
            if (this._predicate()) {
                this._end();
            }
        },

        /**
         * @private
         * Called when this timer has end-of-life, either because a valid size has been detected or because of a
         * timeout.
         *
         * @param {Boolean} [timeout] True on time out, falsey on (earlier) success.
         */
        _end(timeout) { // TODO: Consider using Promise.timeout(..)/Promise.time(..) to handle timeout
            if (timeout === true) {
                log.warn(`Timed out, after ${INVALIDATE_VIEWPORT_SIZE_TIMEOUT} milliseconds, due to invalid viewport size`);
                this.stop("timeout");
            } else {
                log.info("Got a valid viewport size - continuing rendering");
                this.stop("valid");
            }
        }
    });

    /**
     * Class for managing the viewer size (e.g. scale), when embedded in a responsive environment.
     *
     * <pre>
     *
     * var controller = new ResponsiveViewerController({
     *     viewer: viewer
     * });
     *
     * // Call #ready to start the responsive behavior...
     * controller.ready()
     *     .done(function () {
     *         console.info("Successful rendering");
     *     })
     *     .fail(function () {
     *         console.error("Viewport size invalid");
     *     });
     *
     * </pre>
     *
     * @author Kim
     * @class ResponsiveViewerController
     *
     * @cfg {ExperienceViewer} viewer The current <tt>ExperienceViewer</tt> instance.
     */
    const ResponsiveViewerController = Marionette.Controller.extend({

        _experienceSize: null,

        /**
         * @type {ExperienceViewer}
         */
        _viewer: null,

        /**
         * @type {ExperienceModel}
         */
        _experience: null,

        _window: $(window),

        _topWindow: null,

        _embedContainer: null,

        /**
         * @private @type {Size}
         * The fixed view port size, e.g. the size of the QuickView in the editor. Will never change.
         */
        _viewportSize: null,

        /**
         * @private @type {Size}
         * The initial view port size, used when this Viewer/Experience is rendered on an adjacent Scene, with
         * display: none and thus (0,0) viewport. By setting the initial viewport size, the initial rendering
         * will be scaled correct and updated normally on for example resize.
         */
        _viewportInitialSize: null,

        _validSizeTimer: null,

        _viewModel: null,

        _scrollableContainer: null,

        initialize() {
            // If we have a channel bridge here, we are embedded via channels in an iframe loaded via channels...
            const bridge = channelBridge();

            this._viewModel             = this.options.viewer.model;
            this._experience            = this._viewModel.get("experience");
            this._viewportSize          = this.options.viewportSize;
            this._viewportInitialSize   = this.options.viewportInitialSize;
            this._viewer                = this.options.viewer;
            // can still be true for embedded experiences via a widget even if no channel bridge
            this._embeddedInIframe      = (window !== window.top);
            this._experienceSize        = this._experience.getSize();
            this._scrollableContainer   = this.options.scrollableContainer;
            this._containerSizeProvider = this.options.containerSizeProvider || this._containerSizeProvider;

            // TODO: for embedded experiences (recursively), should most of these statements not reference the parent
            //       window instead of the top most, e.g. for scrolling and viewport.

            // Grab a reference to the top level window - some browsers (hint: mobile
            // Safari does not fire resize and orientation change events on framed windows)
            this._topWindow = this._embeddedInIframe ? $(Window.topWindow()) : this._window;

            // Determine whether we need to fetch the viewport size from the parent container
            // (if we're embedded via channels) - we do this to circumvent an issue with iframes not being
            // responsive in mobile Safari (aka. 'isResponsiveIframesSupported'). An embed container
            // supplied via options is used for preview in the editor.
            this._embedContainer = this.options.embedContainer || (bridge && !Browser.capabilities.isResponsiveIframesSupported ? bridge.matchedElement() : null);

            // Only defined if we're embedded via channels...
            this._viewportVisibilityContainer = bridge && bridge.iframeElement();

            // Only defined if we are in the editor preview
            if (this._scrollableContainer) {
                this._scrollableContainer.on("scroll", this.updateIfValid.bind(this));
            }

            // Make sure this controller is not actively checking sizes while the Viewer is for example rendered
            // on the next scene embedded. Notice the "active" property thus in effect applies to the entire
            // Experience, since all Scenes are then inactive, while at most one can be active on true, i.e. the
            // current one. The cause of the "change:active" will be some other Experience calling this embedded
            // one, either directly or indirectly, via is ViewerProxy.state({active: true|false, ..}) method...
            this.listenTo(this._viewModel, "change:active", this.updateIfValid.bind(this));

            // Listen for future required updates, but only when active like described above for "change:active", but
            // this is already handled elsewhere, i.e. these listeners will be inactive when the view model
            // becomes inactive by design...
            this._layoutListener = Window.on({
                event:     "resize orientationchange",
                callback:  this.updateIfValid,
                scope:     this,
                win:       this._topWindow,
                debounce:  300
            });
            this._scrollListener = Window.on({
                event:     "scroll",
                callback:  this.updateIfValid,
                scope:     this,
                win:       this._topWindow,
                throttle:  100
            });
        },

        /**
         * Returns the size of the specified (jQuery wrapped) container.
         *
         * This may be "overridden" if a <tt>containerSizeProvider</tt> is specified in the passed options.
         *
         * @param  {jQueryElement} $container The container to measure.
         * @return {Size}                     The size of the container.
         *
         * @see PreviewExperienceViewer
         */
        _containerSizeProvider($container) {
            return Size.from($container.width(), $container.height());
        },

        _getViewportSize() {
            // If we're embedded, check to see if the size of the iframe is different from it's parent.
            // In theory, they should be the same (size the iframe is sized 100%x100%), but it seems
            // some browsers get it wrong (hint: Mobile Safari, MOS-1489). In that case we return the
            // parent size always, even if identical...
            if (this._embedContainer) {
                return new Size(this._embedContainer.innerWidth(), this._embedContainer.innerHeight());
            }
            // viewport size is fixed, but the initial viewport size is for embedding Experiences, say on
            // adjacent Scenes where the window (iframe) viewport at initialisation time will be (0,0) because
            // the iframe has to be hidden via display:none because of a Webkit bug regarding src=about:blank...
            return this._viewportSize || this._viewportInitialSize || new Size(this._window.width(), this._window.height());
        },

        /**
         * @private
         *
         * Returns <tt>true</tt> if a valid (<tt>width > 0 && height > 0</tt>)
         * viewport size is detected, <tt>false</tt> if not.
         *
         * @return {Boolean} <tt>true</tt> if a valid (<tt>width > 0 && height > 0</tt>)
         * viewport size is detected, <tt>false</tt> if not.
         */
        _validViewportSize() {
            const size = this._getViewportSize();
            return size.width > 0 && size.height > 0;
        },

        /**
         * @private
         * Updates the size if and only if the view port size is valid.
         */
        updateIfValid() {
            // If we have a valid view port size at the time of change, make sure we update...
            const valid = this._validViewportSize();
            if (valid) {
                this._update();
            }

            // Done with the initial size...
            if (this._viewModel.get("active")) {
                this._viewportInitialSize = null;
            }
            return valid;
        },

        // Note: this is still relevant when paused or stalled, but must be ignored when not active...
        _update() {
            const size = this._getViewportSize();
            let newExperienceSize = this._experienceSize;

            // We need a valid size in order to render correctly
            Preconditions.checkState(size.width > 0 && size.height > 0, `Invalid viewport size: ${size.width}x${size.height}`);

            // We don't want to scale up when experiences are viewed in browser via URL
            if (this._embeddedInIframe || (size.width < this._experienceSize.width || size.height < this._experienceSize.height)) {
                newExperienceSize = size.shrinkToAspectRatio(this._experienceSize.getAspectRatio());
            }

            // Perform the transformation
            const scaleX = newExperienceSize.width / this._experienceSize.width;
            const scaleY = newExperienceSize.height / this._experienceSize.height;
            const left = this._getCoordinate("x", size, newExperienceSize);
            const top  = this._getCoordinate("y", size, newExperienceSize);

            // Position and scale the experience
            this._viewer.position(left, top);
            this._experience.set("scale", Scale.from(scaleX, scaleY));
            const experienceOffset = {
                left,
                top
            };
            this._updateViewportVisibility(experienceOffset);
        },

        _updateViewportVisibility(experienceOffset) {
            let visiblePartOfExperience;
            let containerSize;
            let containerOffset;

            if (this._viewportVisibilityContainer) { // TODO: should this not reference the parent window only?
                containerSize = this._containerSizeProvider(this._viewportVisibilityContainer);
                containerOffset = Window.offset(this._viewportVisibilityContainer);

                visiblePartOfExperience = ResponsiveViewerController._getVisiblePartOfExperience(
                    containerSize, containerOffset,
                    experienceOffset, this._topWindow
                );
            } else if (this._scrollableContainer) {
                containerSize = this._containerSizeProvider(this._embedContainer);
                // Means that we are in the preview and have no offset.
                containerOffset = {left: 0, top: 0};

                visiblePartOfExperience = ResponsiveViewerController._getVisiblePartOfExperience(
                    containerSize, containerOffset,
                    experienceOffset, this._scrollableContainer
                );
            } else {
                visiblePartOfExperience = new Rectangle(0, 0, this._experienceSize.width, this._experienceSize.height);
            }

            this._viewModel.set("viewportVisibility", visiblePartOfExperience);
        },

        _getCoordinate(axis, size, experienceSize) {
            let side;
            let alignment;

            if (axis === "x") {
                side = "width";
                alignment = this.options.horizontalAlign || "center";
            } else {
                side = "height";
                alignment = this.options.verticalAlign || "middle";
            }

            switch (alignment) {
                case "left":
                case "top":
                    return 0;

                case "center":
                case "middle":
                    return Math.round((size[side] * 0.5) - (experienceSize[side] * 0.5));

                case "right":
                case "bottom":
                    return Math.round(size[side] - experienceSize[side]);

                default:
                    // Candidate for refactoring during MOS-2934.
                    Preconditions.fail(`DefaultSwitchWarn - The alignment '${alignment}' is not recognized.`);
                    break;
            }
        },

        start() {
            // Set the initial active state as dictated by the view model, which will trigger an update
            // if the viewport is valid...
            const valid = this.updateIfValid();
            let promise;

            // Start time on invalid view port...
            if (!valid) {
                if (this._validSizeTimer) {
                    this._validSizeTimer.stop();
                }
                this._validSizeTimer = new ValidSizeTimer(this._validViewportSize.bind(this));

                // Once the size becomes valid, start listening for responsive changes...
                promise = this._validSizeTimer.start().then((state) => {
                    this._validSizeTimer = null;
                    this.updateIfValid();
                    return state === "valid"; // or "timeout" or "stopped"
                });
            }

            if (!promise) {
                return Promise.resolved(valid);
            }

            return promise;
        },

        onClose() {
            if (this._layoutListener) {
                this._layoutListener.off();
            }
            if (this._scrollListener) {
                this._scrollListener.off();
            }
            if (this._validSizeTimer) {
                this._validSizeTimer.stop();
                this._validSizeTimer = null;
            }

            // Restore original scale...
            this._experience.set("scale", new Scale());
        }
    }, {
        /**
         * Returns a rectangle specifying the visible part of the experience. If the experience
         * outside the window, and thus invisible an empty rectangle is returned, i.e.
         * rectangle.isEmpty() === true.
         *
         * TODO MOS-2620: The size of the experience is not taken into account, so the rectangle
         * might exceed the right and the bottom part of the experience. This is currently
         * acceptable since the returned value is only used to determine what widgets are currently
         * visible.
         *
         * @param {Size} containerSize The size of the current container.
         * the container. If the experience is embedded this is the iframe. If it's running it full
         * screen this is the web page.
         * @param {Offset} containerOffset The position of the container relative to the web page.
         * @param {Offset} experienceOffset The position of the experience relative to the
         * container.
         * @param {jQuery} topWindow The browser window.
         * @returns {Rectangle}
         */
        _getVisiblePartOfExperience(containerSize, containerOffset, experienceOffset, topWindow) {
            const viewportHeight = topWindow.height();
            const viewportTop = topWindow.scrollTop();
            const viewportBottom = viewportTop + viewportHeight;
            const containerHeight = containerSize.height;
            const containerTop = containerOffset ? (containerOffset.top + experienceOffset.top) : 0;
            const containerBottom = containerTop + containerHeight;
            const verticalOverlapFraction = MathUtil.overlapFraction(containerTop, containerBottom, viewportTop, viewportBottom);

            const top = MathUtil.between(viewportTop - containerTop, 0, containerHeight);
            const height = MathUtil.between(verticalOverlapFraction * viewportHeight, 0, viewportHeight);

            const viewportWidth = topWindow.width();
            const viewportLeft = topWindow.scrollLeft();
            const viewportRight = viewportLeft + viewportWidth;
            const containerWidth = containerSize.width;
            const containerLeft = containerOffset ? (containerOffset.left + experienceOffset.left) : 0;
            const containerRight = containerLeft + containerWidth;
            const horizontalOverlapFraction = MathUtil.overlapFraction(containerLeft, containerRight, viewportLeft, viewportRight);

            const left = MathUtil.between(viewportLeft - containerLeft, 0, containerWidth);
            const width = MathUtil.between(horizontalOverlapFraction * viewportWidth, 0, viewportWidth);

            const visiblePartOfExperience = new Rectangle(top, left, width, height);
            return visiblePartOfExperience;
        }
    });

    return ResponsiveViewerController;
});



// WEBPACK FOOTER //
// ./src/shared/ui/viewer/ResponsiveViewerController.js